"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

type CaseType = "upper" | "lower" | "title" | "sentence" | "camel" | "pascal" | "snake" | "kebab";

export function CaseConverter() {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [selectedCase, setSelectedCase] = useState<CaseType>("upper");
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "case-converter") ?? tools[0],
    locale
  );

  const convertCase = (text: string, caseType: CaseType): string => {
    if (!text) return "";

    switch (caseType) {
      case "upper":
        return text.toUpperCase();
      case "lower":
        return text.toLowerCase();
      case "title":
        return text.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
      case "sentence":
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      case "camel":
        return text
          .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
            index === 0 ? word.toLowerCase() : word.toUpperCase()
          )
          .replace(/\s+/g, "");
      case "pascal":
        return text.replace(/(?:^\w|[A-Z]|\b\w)/g, (word) => word.toUpperCase()).replace(/\s+/g, "");
      case "snake":
        return text.toLowerCase().replace(/\s+/g, "_");
      case "kebab":
        return text.toLowerCase().replace(/\s+/g, "-");
      default:
        return text;
    }
  };

  const handleConvert = () => {
    setOutputText(convertCase(inputText, selectedCase));
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(outputText);
  };

  const caseOptions = [
    { value: "upper", label: "UPPER CASE", icon: "mdi:format-letter-case-upper" },
    { value: "lower", label: "lower case", icon: "mdi:format-letter-case-lower" },
    { value: "title", label: "Title Case", icon: "mdi:format-title" },
    { value: "sentence", label: "Sentence case", icon: "mdi:format-letter-case" },
    { value: "camel", label: "camelCase", icon: "mdi:code-tags" },
    { value: "pascal", label: "PascalCase", icon: "mdi:code-braces" },
    { value: "snake", label: "snake_case", icon: "mdi:underscore" },
    { value: "kebab", label: "kebab-case", icon: "mdi:minus" },
  ] as const;

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
             <Icon icon="mdi:format-letter-case" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {caseOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setSelectedCase(option.value as CaseType)}
            className={`flex items-center gap-2 rounded-[15px] border-2 p-3 text-sm font-bold transition-all ${
              selectedCase === option.value
                ? "border-positivus-dark bg-positivus-dark text-white shadow-[0_4px_0_0_#191A23] translate-y-[-2px]"
                : "border-positivus-dark/20 bg-white text-positivus-dark hover:border-positivus-dark hover:shadow-[0_4px_0_0_#191A23] hover:translate-y-[-2px]"
            }`}
          >
            <Icon icon={option.icon} className="h-4 w-4" />
            <span className="hidden sm:inline">{option.label}</span>
          </button>
        ))}
      </div>

      <div className="space-y-6">
        <div>
          <label className="mb-3 block text-lg font-medium text-positivus-dark">{ui.inputText}</label>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={ui.enterYourTextHere}
            className="min-h-[200px] w-full rounded-[20px] border-2 border-positivus-dark/20 bg-white p-5 text-positivus-dark placeholder:text-gray-400 focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all"
          />
        </div>

        <button
          onClick={handleConvert}
          className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
        >
          {ui.convert}
        </button>

        {outputText && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-lg font-medium text-positivus-dark">{ui.output}</label>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 rounded-[10px] bg-positivus-green/20 px-3 py-1.5 text-sm font-bold text-positivus-dark hover:bg-positivus-green hover:text-white transition-colors"
                title={ui.copyToClipboard}
              >
                <Icon icon={uiIcons.copy} className="h-4 w-4" />
                {ui.copy}
              </button>
            </div>
            <div className="min-h-[200px] w-full rounded-[20px] border-2 border-positivus-dark bg-gray-50 p-5">
              <pre className="whitespace-pre-wrap font-mono text-sm text-positivus-dark">{outputText}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
